import { useEffect, useRef, useState } from "react";
import MasterLayout from "./MasterLayout";
import { masterFetch } from "./masterApi";

export default function MasterSupport() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const pollRef = useRef(null);
  const bottomRef = useRef(null);

  async function loadThreads() {
    try { setThreads((await masterFetch("/support-chat/threads")).threads || []); }
    catch (e) { setErr(e.message); }
  }

  async function loadThread(id) {
    if (!id) return;
    try {
      const data = await masterFetch(`/support-chat/threads/${id}`);
      setMessages(data.messages || []);
      // mark as read
      masterFetch(`/support-chat/threads/${id}/mark-read`, { method: "POST" }).catch(()=>{});
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { setErr(e.message); }
  }

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!selected) return;
    loadThread(selected);
    pollRef.current = setInterval(() => { loadThread(selected); loadThreads(); }, 4000);
    return () => clearInterval(pollRef.current);
  }, [selected]);

  async function send() {
    const c = draft.trim();
    if (!c || !selected) return;
    setDraft("");
    try {
      await masterFetch(`/support-chat/threads/${selected}/messages`, {
        method: "POST", body: JSON.stringify({ content: c })
      });
      await loadThread(selected);
    } catch (e) { alert(e.message); }
  }

  return (
    <MasterLayout>
      <h1 style={{ margin: "0 0 4px", fontSize: 26, letterSpacing: -0.5 }}>Support chat</h1>
      <div style={{ opacity: 0.55, fontSize: 13, marginBottom: 20 }}>Direct line to each tenant. Polled every 4s.</div>

      {err && <div style={{ padding: 12, background: "rgba(255,80,90,0.1)", border: "1px solid rgba(255,80,90,0.4)", color: "#ff7886", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{err}</div>}

      <div data-testid="support-shell" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 14, height: "65vh" }}>
        {/* Left: threads */}
        <div style={{ background: "#0f0a1c", border: "1px solid #1f1232", borderRadius: 12, overflow: "auto" }}>
          {threads.length === 0 && <div style={{ padding: 20, opacity: 0.4, fontSize: 13 }}>No threads yet.</div>}
          {threads.map(t => (
            <div key={t.id} onClick={() => setSelected(t.id)} data-testid={`thread-${t.id}`} style={{
              padding: 14,
              cursor: "pointer",
              borderBottom: "1px solid #1a1029",
              background: selected === t.id ? "rgba(139,92,246,0.12)" : "transparent",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontWeight: 500, fontSize: 13 }}>{t.tenant_name}</span>
                {t.unread_for_master > 0 && <span style={{ background: "#8b5cf6", color: "#fff", borderRadius: 999, fontSize: 10, padding: "2px 7px" }}>{t.unread_for_master}</span>}
              </div>
              <div style={{ opacity: 0.5, fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t.last_message || "(no messages)"}
              </div>
            </div>
          ))}
        </div>

        {/* Right: conversation */}
        <div style={{ background: "#0f0a1c", border: "1px solid #1f1232", borderRadius: 12, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {!selected ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.4, fontSize: 13 }}>Select a thread →</div>
          ) : (
            <>
              <div data-testid="messages" style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                {messages.map(m => (
                  <div key={m.id} style={{
                    alignSelf: m.sender_role === "master" ? "flex-end" : "flex-start",
                    maxWidth: "70%",
                    background: m.sender_role === "master" ? "linear-gradient(135deg,#8b5cf6,#a855f7)" : "rgba(255,255,255,0.06)",
                    color: "#fff",
                    padding: "9px 14px",
                    borderRadius: 14,
                    fontSize: 13,
                  }}>
                    <div style={{ opacity: 0.6, fontSize: 10, marginBottom: 3 }}>{m.sender_label} · {new Date(m.created_at).toLocaleTimeString()}</div>
                    {m.content}
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div style={{ borderTop: "1px solid #1a1029", padding: 12, display: "flex", gap: 8 }}>
                <input
                  data-testid="chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a reply…"
                  style={{ flex: 1, padding: "9px 12px", background: "#06050d", border: "1px solid #3a2a55", borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "inherit" }}
                />
                <button data-testid="chat-send" onClick={send} style={{ padding: "9px 18px", background: "linear-gradient(135deg,#8b5cf6,#a855f7)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "inherit" }}>Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </MasterLayout>
  );
}
